//Built-in libraries
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
//Services
import { useDashboardData } from '../../../hooks/ReactQueryHooks/useDashboardQuery';
//Assets and Miscellaneous
import type { DashboardType } from '../../../types/Analytics';
import Table from '../../UI/Table';
import { Widget } from '../constants';
//Custom components
import WidgetLayout from '../WidgetLayout';

interface DistrictImpactWidgetProps {
  dashboard: DashboardType;
}

const DistrictImpactWidget = ({ dashboard }: DistrictImpactWidgetProps) => {
  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [totalPage, _setTotalPage] = useState<number>(1);

  const fetchDistrictImpactWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_BY_NEIGHBORHOOD_DISTRICT,
  });

  const districtImpactWidgetData = fetchDistrictImpactWidgetData?.data?.data;

  const nextPageHandler = () => {
    setPage((prevState) => (prevState === totalPage ? totalPage : page + 1));
  };

  const previousPageHandler = () => {
    setPage((prevState) => (prevState > 1 ? page - 1 : 1));
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'name',
      header: () => <div className='uppercase'>District</div>,
      accessorKey: 'name',
      cell: (props) => {
        const { getValue } = props;
        return getValue() as string;
      },
    },
    {
      id: '_count',
      header: () => <div className='text-center uppercase'>Projects</div>,
      accessorKey: '_count',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-center'>
            <span>{getValue() as number}</span>
          </div>
        );
      },
    },
    {
      id: 'totalDevelopmentCost',
      header: () => <div className='text-center uppercase'>Total Development Value</div>,
      accessorKey: 'totalDevelopmentCost',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-center'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </div>
        );
      },
    },
  ];

  const tablePagination = {
    page: page,
    pageSize: perPage,
    pageCount: totalPage,
    currentPage: page,
    setPageSize: (v: number) => setPerPage(v),
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  if (!districtImpactWidgetData || districtImpactWidgetData.length === 0) {
    return null;
  }

  return (
    <div className='col-span-4 2xl:col-span-2'>
      <WidgetLayout dashboard={dashboard} widget={Widget.PROJECTS_BY_NEIGHBORHOOD_DISTRICT}>
        {
          <div className='h-full w-full'>
            <div className='flex flex-row  bg-white justify-start items-center rounded-t-md py-4'>
              <h3 className='font-bold text-bold border-l-4 p-4 border-green-500 capitalize'>District Impact</h3>

              <div className='pr-2 text-neutralColor-light cursor-pointer hover:text-neutralColor-medium transition duration-300 ease-in-out'></div>
            </div>
            <div className='h-96 border rounded-b-md w-full bg-white'>
              <Table cols={tableColumns} tableData={districtImpactWidgetData} pagination={tablePagination} height='h-5/6' />
            </div>
          </div>
        }
      </WidgetLayout>
    </div>
  );
};

export default DistrictImpactWidget;
